import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import {
  Button,
  Container,
  Dimmer,
  Dropdown,
  Header,
  Icon,
  Label,
  Loader,
  Menu,
  Modal,
  Table,
} from 'semantic-ui-react';
import { connect } from 'react-redux';
import { replace } from 'react-router-redux';
import config from '../../../../config';
import { fetchM } from '../../../actions/fetchM';
import { flashSuccessMessage, flashErrorMessage, flashMessage } from '../../../actions/flash';
import { decodeSearch, encodeSearch } from '../../../utils/url';
import isNumber from '../../../utils/numbers/isNumber';
import fileSize from '../../../utils/fileSize';
import PageNav from '../../../components/PageNav';
import NavBar from '../../../components/NavBar';
import AddDocument from '../../modals/AddDocument';
import { editRoutes } from '../navigations';
import { TRANSLATION_FILES, namespaces } from '../../../constants/translation';

class List extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    documentToken: PropTypes.string,
    id: PropTypes.string,
    location: PropTypes.shape({
      query: PropTypes.object,
      search: PropTypes.string,
      pathname: PropTypes.string,
    }),
    appConfig: PropTypes.shape({
      data: PropTypes.shape({
        lawyerIsEnabled: PropTypes.bool,
      }),
    }),
    auth: PropTypes.shape({
      company: PropTypes.object,
    }),
    t: PropTypes.func.isRequired,
  }
  static mapStateToProps = state => ({
    auth: state.auth,
    appConfig: state.appConfig,
  })
  static getPropsToState = (props) => {
    const loading = props.loading;
    const count = props.count;
    const limit = props.limit;
    const offset = props.offset;
    const documents = props.documents || [];
    const query = decodeSearch(props.location.search);
    const currentPage = parseInt(query.page, 10) || 1;
    const menuIsTab = window.innerWidth <= 590;
    return {
      documents,
      loading,
      count,
      limit,
      offset,
      currentPage,
      menuIsTab,
    };
  }
  constructor(props) {
    super(props);
    const state = List.getPropsToState(props);
    this.state = {
      ...state,
      options: [],
      selectedFile: null,
      showRemove: false,
      showAddDocument: false,
      loading: false,
      project: [],
    };
  }
  componentDidMount() {
    window.addEventListener('resize', this.handleResize, false);
    this.fetchProject();
  }
  componentWillReceiveProps(nextProps) {
    const state = List.getPropsToState(nextProps);
    this.setState({ ...state });
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize, false);
  }
  setLoading = (loading = false) => (new Promise((res) => {
    this.setState({ loading }, res);
  }))
  async fetchProject() {
    await this.setLoading(true);
    const { store } = this.context;
    const { t } = this.props;
    let res;
    try {
      res = await store.dispatch(fetchM(`/projects/${this.props.id}/`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const project = await res.json();
      await this.setState({
        project,
      });
    }
    await this.setLoading(false);
  }
  handleResize = () => {
    const menuIsTab = window.innerWidth <= 590;
    if (menuIsTab !== this.state.menuIsTab) {
      this.setState({ menuIsTab });
    }
  }
  replaceQuery(query) {
    const { store } = this.context;
    store.dispatch(replace(`/projects/${this.props.id}/documents/?${encodeSearch(query)}`));
  }
  handleChangePage = (page) => {
    const query = decodeSearch(this.props.location.search);
    if (!query.page || !isNumber(query.page) || query.page <= 0) {
      query.page = 1;
    }
    const currentPage = parseInt(page || query.page, 10);
    query.page = currentPage;
    this.setState({ currentPage });
    this.replaceQuery(query);
  }
  handleCancelRemove = (e) => {
    e.preventDefault();
    this.setState({
      showRemove: false,
      selectedFile: null,
    });
  }
  handleRemove = (e, selectedFile) => {
    e.preventDefault();
    this.setState({
      selectedFile,
    }, () => {
      this.setState({
        showRemove: true,
      });
    });
  }
  handleConfirmRemove = (e) => {
    const { store } = this.context;
    const { project, documents } = this.state;
    e.preventDefault();
    this.setState({
      showRemove: false,
    }, () => {
      if (project && (project.marketplaceIsPublish || project.marketplaceBuyerIsPublish) && documents.length === 1) {
        return store.dispatch(flashMessage({
          header: 'Error',
          message: 'Document is required to launch project to Marketplace',
          icon: 'warning sign',
          type: 'error',
        }));
      }
      return this.removeDocument();
    });
  }
  addDocument() {
    const { store } = this.context;
    const { t } = this.props;
    store.dispatch(fetchM(`/projects/${this.props.id}/documents/${this.state.selectedFile.id}/`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Csrf-Token': this.props.documentToken,
      },
    }))
      .catch((err) => {
        store.dispatch(flashErrorMessage(err));
      })
      .then(() => {
        this.handleChangePage();
        store.dispatch(flashSuccessMessage({ header: t('Add Success', { ns: TRANSLATION_FILES.translation }), message: t('Document successfully added to project', { ns: TRANSLATION_FILES.translation }) }));
      });
  }
  async removeDocument() {
    const { store } = this.context;
    const { t } = this.props;
    let res;
    try {
      res = await store.dispatch(fetchM(`/projects/${this.props.id}/documents/${this.state.selectedFile.id}/`, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': this.props.documentToken,
        },
      }));
    } catch (err) {
      await this.setState({ selectedFile: null, loading: false });
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      store.dispatch(flashErrorMessage(err));
      return;
    }
    this.setState({
      selectedFile: null,
      loading: false,
    });
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      this.handleChangePage();
      store.dispatch(flashSuccessMessage({ header: t('Remove Success', { ns: TRANSLATION_FILES.translation }), message: t('Document successfully deleted', { ns: TRANSLATION_FILES.translation }) }));
    }
  }
  handleUpload = body => ({
    url: `/projects/${this.props.id}/documents/`,
    options: {
      credentials: 'include',
      method: 'POST',
      headers: {
        // Let browser decide Content-Type
        'X-Csrf-Token': this.props.documentToken,
      },
      body,
    },
  })
  handleShowAddDocument = (e) => {
    e.preventDefault();
    this.setState({
      showAddDocument: true,
    });
  }
  handleCloseAddDocument = (e) => {
    if (e) {
      e.preventDefault();
    }
    this.handleChangePage();
    this.setState({
      showAddDocument: false,
    });
  }
  handleAddDocument = (e, selectedFile) => {
    e.preventDefault();
    this.setState({
      showAddDocument: false,
      selectedFile,
    }, () => {
      this.addDocument();
    });
  }
  handleCheck = (data) => {
    if (data.projects && data.projects.length > 0) {
      return data.projects.some(p => p.id === this.props.id);
    }
    return false;
  }
  handleMenu = (e, { value }) => {
    const { store } = this.context;
    if (value.includes(config.get('developerURL'))) {
      e.preventDefault();
      window.location.href = value;
    } else {
      store.dispatch(replace(value));
    }
  }
  render() {
    const { auth, id, location, t } = this.props;
    const { pathname } = location;
    const companyServiceID = auth && auth.company && auth.company.service_company_id ? auth.company.service_company_id : '';
    const isMalaysia = auth && auth.company && auth.company.countryCode.toUpperCase() === 'MY';
    const links = editRoutes(id, this.props.appConfig.data && this.props.appConfig.data.lawyerIsEnabled, isMalaysia, companyServiceID).map(l => ({
      ...l,
      text: t(l.text, { ns: l.text === 'Edit' ? TRANSLATION_FILES.translation : TRANSLATION_FILES.screens }),
    }));
    return (
      <Container>
        <NavBar
          links={
            this.state.menuIsTab ?
              <Dropdown
                item
                selectOnBlur={false}
                options={links}
                onChange={this.handleMenu}
                value={pathname}
              />
            : links.map((link, key) => (
              <Menu.Item
                key={key}
                name={link.text}
                active={pathname === link.value}
                onClick={e => this.handleMenu(e, link)}
              />
            ))
          }
          rightNav={
            <Menu.Item fitted>
              <Button
                onClick={this.handleShowAddDocument}
                disabled={this.state.loading}
                primary
                icon="add"
                content={t('Add', { ns: TRANSLATION_FILES.screens })}
              />
            </Menu.Item>
          }
        />
        <AddDocument
          open={this.state.showAddDocument}
          onClose={this.handleCloseAddDocument}
          onConfirm={this.handleAddDocument}
          onUpload={this.handleUpload}
          onCheck={this.handleCheck}
        />
        <Modal
          open={this.state.showRemove}
          onClose={this.handleCancelRemove}
        >
          <Header icon="warning sign" content={t('Confirm remove?', { ns: TRANSLATION_FILES.translation })} />
          <Modal.Content>
            <Modal.Description>
              <Header
                as="h3"
                content={t('Are you sure to remove this file?', { ns: TRANSLATION_FILES.translation })}
                subheader={this.state.selectedFile ? this.state.selectedFile.name : ''}
              />
            </Modal.Description>
          </Modal.Content>
          <Modal.Actions>
            <Button basic negative onClick={this.handleCancelRemove} icon="cancel" content={t('No', { ns: TRANSLATION_FILES.screens })} inverted />
            <Button basic positive onClick={this.handleConfirmRemove} icon="remove" content={t('Yes', { ns: TRANSLATION_FILES.screens })} inverted />
          </Modal.Actions>
        </Modal>
        <Dimmer
          inverted style={{ zIndex: 20 }}
          active={this.state.loading && this.state.documents.length > 0}
        >
          <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
        </Dimmer>
        <Table
          striped unstackable
          color="blue"
          selectable
        >
          <Table.Header fullWidth>
            <Table.Row>
              <Table.HeaderCell colSpan={4} textAlign="right">
                <PageNav
                  currentPage={this.state.currentPage}
                  count={this.state.count}
                  limit={this.state.limit}
                  offset={this.state.offset}
                  dataSize={this.state.documents.length}
                  loading={this.state.loading}
                  onChange={this.handleChangePage}
                />
              </Table.HeaderCell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell>{t('Name', { ns: TRANSLATION_FILES.translation })}</Table.HeaderCell>
              <Table.HeaderCell>{t('Size', { ns: TRANSLATION_FILES.translation })}</Table.HeaderCell>
              <Table.HeaderCell>{t('Tags', { ns: TRANSLATION_FILES.translation })}</Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {
              this.state.documents.length > 0 ?
                this.state.documents.map((file, key) => (
                  <Table.Row key={key}>
                    <Table.Cell width={6}>
                      <a
                        href={`${config.get('bookApiURL')}/documents/${file.id}/view/${encodeURIComponent(file.name)}.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Icon name="file pdf outline" />
                        {file.name}
                      </a>
                    </Table.Cell>
                    <Table.Cell collapsing>
                      {fileSize(file.size)}
                    </Table.Cell>
                    <Table.Cell>
                      <Label.Group size="tiny">
                        {
                          file.tags && file.tags.length > 0 ?
                          file.tags.map((t, idx) => (
                            <Label key={idx} as="a">{t}</Label>
                          ))
                          : <p>{t('No tags set', { ns: TRANSLATION_FILES.translation })}</p>
                        }
                      </Label.Group>
                    </Table.Cell>
                    <Table.Cell collapsing textAlign="right">
                      <Button
                        size="tiny"
                        compact
                        negative
                        icon="remove"
                        disabled={this.state.loading}
                        onClick={e => this.handleRemove(e, file)}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              :
                <Table.Row>
                  <Table.Cell colSpan={4} textAlign="center">
                    {
                      this.state.loading ?
                        <Loader active inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
                      : t('No documents found.', { ns: TRANSLATION_FILES.translation })
                    }
                  </Table.Cell>
                </Table.Row>
            }
          </Table.Body>
          <Table.Footer fullWidth>
            <Table.Row>
              <Table.HeaderCell colSpan={4} textAlign="right">
                <PageNav
                  currentPage={this.state.currentPage}
                  count={this.state.count}
                  limit={this.state.limit}
                  offset={this.state.offset}
                  dataSize={this.state.documents.length}
                  loading={this.state.loading}
                  onChange={this.handleChangePage}
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </Container>
    );
  }
}


export default withTranslation(namespaces)(connect(List.mapStateToProps)(List));



// WEBPACK FOOTER //
// ./src/containers/Project/Documents/List.jsx